import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MessageSquare, Calendar, Loader2 } from 'lucide-react';
import moment from 'moment';
import { SectionCard } from './SectionCard';
import { StarRating } from './StarRating';
import { NoDataFound } from './NoDataFound';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IReview, IReviewListResponse, ReviewTargetType } from '@/interfaces/Review/review.interface';
import { formatDateTime } from '@/utility/helper';

interface ReviewCardProps {
    targetType: ReviewTargetType;
    targetId: string;
    title?: string;
    className?: string;
    maxHeight?: string;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
    targetType,
    targetId,
    title = "Reviews & Ratings",
    className = "",
    maxHeight = "500px"
}) => {
    const [reviews, setReviews] = useState<IReview[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastReviewElementRef = useCallback((node: HTMLDivElement) => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        }, {
            root: containerRef.current,
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore]);

    const fetchReviews = async (pageNum: number, isInitial: boolean = false) => {
        if (!targetId) return;

        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        try {
            const response = await callApi<IReviewListResponse>(
                `${API_URL}${API_ENDPOINT.review}?targetType=${targetType}&targetId=${targetId}&page=${pageNum}&limit=10`,
                { method: 'GET' }
            );

            const newReviews = response.data?.reviews || [];
            if (isInitial) {
                setReviews(newReviews);
            } else {
                setReviews(prev => [...prev, ...newReviews]);
            }

            setTotal(response.data?.pageInfo?.total || 0);
            setHasMore(newReviews.length > 0 && (response.data?.pageInfo?.page < response.data?.pageInfo?.totalPages));
        } catch (error) {
            console.error("Error fetching reviews:", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setReviews([]);
        setHasMore(true);
        fetchReviews(1, true);
    }, [targetId, targetType]);

    useEffect(() => {
        if (page > 1) {
            fetchReviews(page);
        }
    }, [page]);

    return (
        <SectionCard
            title={title}
            icon={<MessageSquare />}
            className={className}
            extra={
                <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                    {total} Total
                </span>
            }
        >
            {loading ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="animate-spin h-6 w-6 border-primary" />
                </div>
            ) : reviews.length > 0 ? (
                <div
                    ref={containerRef}
                    className="space-y-4 overflow-y-auto pr-2 custom-scrollbar"
                    style={{ maxHeight }}
                >
                    {reviews.map((review, index) => {
                        const isLast = reviews.length === index + 1;
                        return (
                            <div
                                key={review.id}
                                ref={isLast ? lastReviewElementRef : null}
                                className="p-4 rounded-2xl border border-secondary/10 bg-secondary/5 transition-all hover:border-primary/20 hover:shadow-sm"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-bold text-foreground">
                                            {review.user?.fullName || "Anonymous"}
                                        </span>
                                        <StarRating rating={review.rating} starSize="w-3 h-3" />
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                                        <Calendar className="w-3 h-3" />
                                        {formatDateTime(review.createdAt)}
                                    </div>
                                </div>
                                {review.comment && (
                                    <p className="text-[11px] text-muted-foreground leading-relaxed italic mt-2" title={review.comment}>
                                        "{review.comment}"
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    {loadingMore && (
                        <div className="flex justify-center py-2">
                            <Loader2 className="animate-spin h-4 w-4 text-primary" />
                        </div>
                    )}
                </div>
            ) : (
                <NoDataFound
                    title="No Reviews"
                    description={`No ratings found for this ${targetType.split('_')[1] || 'item'} yet.`}
                />
            )}
        </SectionCard>
    );
};
